import React from 'react';
import Button from '../../../components/Button';
import { Project } from '../../../services/projects';

interface PropertyDetailsTabProps {
  project: Project;
  editingSpecs: boolean;
  propertyValues: {
    squareFootage: string;
    ceilingHeight: string;
  };
  editingDealTerms: boolean;
  dealTermsValue: string;
  setEditingSpecs: React.Dispatch<React.SetStateAction<boolean>>;
  setPropertyValues: React.Dispatch<React.SetStateAction<{
    squareFootage: string;
    ceilingHeight: string;
  }>>;
  setEditingDealTerms: React.Dispatch<React.SetStateAction<boolean>>;
  setDealTermsValue: React.Dispatch<React.SetStateAction<string>>;
  handleSavePropertySpecs: () => Promise<void>;
  handleSaveDealTerms: () => Promise<void>;
  formatFileSize: (size: number) => string;
}

const PropertyDetailsTab: React.FC<PropertyDetailsTabProps> = ({
  project,
  editingSpecs,
  propertyValues,
  editingDealTerms,
  dealTermsValue,
  setEditingSpecs,
  setPropertyValues,
  setEditingDealTerms,
  setDealTermsValue,
  handleSavePropertySpecs,
  handleSaveDealTerms,
  formatFileSize
}) => {
  return (
    <div>
      <h3 style={{ fontSize: '1.4rem', marginBottom: '1.5rem', color: '#1e1e1e' }}>Property Details</h3>
      
      {/* Property Specifications */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ fontSize: '1.1rem', color: '#1e1e1e' }}>Property Specifications</h4>
          <Button 
            onClick={() => setEditingSpecs(!editingSpecs)}
            style={{ 
              padding: '0.5rem 1rem',
              fontSize: '0.9rem'
            }}
          >
            {editingSpecs ? 'Cancel' : 'Edit'}
          </Button>
        </div>
        
        {editingSpecs ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>Square Footage</label>
              <input
                type="number"
                value={propertyValues.squareFootage}
                onChange={(e) => setPropertyValues({...propertyValues, squareFootage: e.target.value})}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666' }}>Ceiling Height (ft)</label>
              <input
                type="number"
                value={propertyValues.ceilingHeight}
                onChange={(e) => setPropertyValues({...propertyValues, ceilingHeight: e.target.value})}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Button onClick={handleSavePropertySpecs}>Save Specifications</Button>
            </div>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            backgroundColor: '#f9f9f9',
            padding: '1.5rem',
            borderRadius: '4px'
          }}>
            <div>
              <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 0.25rem 0' }}>Square Footage</p>
              <p style={{ color: '#333', fontSize: '1.1rem', fontWeight: 500, margin: 0 }}>
                {project.propertyDetails?.squareFootage ? 
                  `${project.propertyDetails.squareFootage.toLocaleString()} sq ft` : 
                  'Not specified'}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 0.25rem 0' }}>Ceiling Height</p>
              <p style={{ color: '#333', fontSize: '1.1rem', fontWeight: 500, margin: 0 }}>
                {project.propertyDetails?.ceilingHeight ? 
                  `${project.propertyDetails.ceilingHeight} ft` : 
                  'Not specified'}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 0.25rem 0' }}>Business Type</p>
              <p style={{ color: '#333', fontSize: '1.1rem', fontWeight: 500, margin: 0 }}>
                {project.businessType || 'Not specified'}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Deal Terms */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ fontSize: '1.1rem', color: '#1e1e1e' }}>Deal Terms</h4>
          <Button 
            onClick={() => setEditingDealTerms(!editingDealTerms)}
            style={{ 
              padding: '0.5rem 1rem',
              fontSize: '0.9rem'
            }}
          >
            {editingDealTerms ? 'Cancel' : 'Edit'}
          </Button>
        </div>
        
        {editingDealTerms ? (
          <div>
            <textarea
              value={dealTermsValue}
              onChange={(e) => setDealTermsValue(e.target.value)}
              style={{ 
                width: '100%', 
                minHeight: '100px', 
                padding: '0.75rem',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                fontSize: '1rem',
                lineHeight: '1.5',
                marginBottom: '1rem'
              }}
              placeholder="Enter deal terms and conditions"
            />
            <Button onClick={handleSaveDealTerms}>Save Deal Terms</Button>
          </div>
        ) : (
          <div style={{ 
            backgroundColor: '#f9f9f9',
            padding: '1.5rem',
            borderRadius: '4px'
          }}>
            <p style={{ color: '#333', margin: 0, whiteSpace: 'pre-wrap' }}>
              {project.dealTerms || 'No deal terms specified.'}
            </p>
          </div>
        )}
      </div>
      
      {/* Location Optimization */}
      <div style={{ marginBottom: '2rem' }}>
        <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#1e1e1e' }}>Location Optimization</h4>
        <div style={{ 
          backgroundColor: '#f9f9f9',
          padding: '1.5rem',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#333', marginBottom: '1rem' }}>
            Our AI can analyze your business type and current location to suggest better nearby alternatives.
          </p>
          <Button 
            disabled={true}
            style={{ 
              backgroundColor: '#e6f7ff',
              color: '#1890ff',
              cursor: 'not-allowed'
            }}
          >
            Find Better Location Nearby (AI Coming Soon)
          </Button>
          <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
            This feature will suggest optimized nearby alternatives based on foot traffic, safety, and market demand.
          </p>
        </div>
      </div>
      
      {/* Documents */}
      <div>
        <h4 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#1e1e1e' }}>Property Documents</h4>
        
        {project.documents && project.documents.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Document Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Type</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Size</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Upload Date</th>
                  <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {project.documents.map((doc) => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '1rem' }}>{doc.name}</td>
                    <td style={{ padding: '1rem' }}>{doc.type}</td>
                    <td style={{ padding: '1rem' }}>{formatFileSize(doc.size)}</td>
                    <td style={{ padding: '1rem' }}>
                      {new Date(doc.uploadDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button 
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.85rem'
                          }}
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          View
                        </Button>
                        <Button 
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.85rem',
                            backgroundColor: '#fff',
                            color: '#1e1e1e',
                            border: '1px solid #1e1e1e'
                          }}
                          onClick={() => {
                            const a = document.createElement('a');
                            a.href = doc.url;
                            a.download = doc.name;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                        >
                          Download
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#666', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '4px' 
          }}>
            <p>No documents uploaded yet.</p>
            <Button 
              style={{ 
                marginTop: '1rem'
              }}
            >
              Upload Document
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyDetailsTab;
